import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import NavbarOverlay from "../common/NavbarOverlay";
import 'bootstrap/js/dist/tab';
import DummySidebar from "./DummySidebar";
import DummyStatsBar from "./DummyStatsBar";
import DummySummaryLayout from "./DummySummaryLayout";
import DummyCategoryLayout from "./DummyCategoryLayout";
import DummyInitialTasksLayout from "./DummyInitialTasksLayout";
import {
  Overlay,
  emptyOverlay,
  computeTasks,
  computeInitialTasks,
  computeCategoryInfo,
} from "./dummyEngine";
import type { Task, Category, CategoryInfo, CategorizationDetails, UserInfo } from "../kategoryzacja/Kategoryzacja";

const API_ROOT = process.env.REACT_APP_API_URL;

// localStorage key holding the edit overlay. Versioned so a schema change (or a
// deliberate reset) is clean.
const STORAGE_KEY = "przykladowy-arkusz-v1";

const loadOverlay = (): Overlay => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyOverlay();
    const parsed = JSON.parse(raw);
    return {
      tasks: parsed.tasks ?? {},
      initial: parsed.initial ?? {},
    };
  } catch {
    return emptyOverlay();
  }
};

const DEFAULT_CATEGORY_INFO: CategoryInfo = {
  category: 'POLOWA',
  nextCategory: 'POLOWA',
  tokens: { polowa: 0, lesna: 0, puszczanska: 0 },
  missingTokens: { lesna: 0, puszczanska: 0 },
  points: 0,
};

const DummyKategoryzacja = ({ userinfo }: { userinfo: UserInfo | null }) => {
    const [showStarredOnly, setShowStarredOnly] = useState(false);
    const [activeCategory, setActiveCategory] = useState<number>(0);

    const [categorizationDetails, setCategorizationDetails] = useState<CategorizationDetails>();
    // Base = the unfilled real sheet from the ungated /raw endpoints.
    const [baseTasklist, setBaseTasklist] = useState<Array<Category>>([]);
    const [baseInitialTasklist, setBaseInitialTasklist] = useState<Array<Task>>([]);
    // Overlay = the user's local, unsaved edits.
    const [overlay, setOverlay] = useState<Overlay>(loadOverlay());

    const [noActiveCategorization, setNoActiveCategorization] = useState(false);

    // The example sheet is never locked - it is always editable.
    const locked = false;

    // Derived, filled-in sheet: base definitions + overlay values, rescored.
    const tasklist = useMemo(() => computeTasks(baseTasklist, overlay), [baseTasklist, overlay]);
    const initialTasklist = useMemo(() => computeInitialTasks(baseInitialTasklist, overlay), [baseInitialTasklist, overlay]);
    const categoryInfo = useMemo(
      () => (categorizationDetails ? computeCategoryInfo(tasklist, categorizationDetails) : DEFAULT_CATEGORY_INFO),
      [tasklist, categorizationDetails]
    );

    // Load the real active categorization once (public, no auth needed).
    useEffect(() => {
      updateCategorizationDetails();
      updateInitialTasklist();
      updateTasklist();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist the overlay on every change.
    useEffect(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(overlay));
      } catch {
      }
    }, [overlay]);

    const updateCategorizationDetails = async () => {
      try {
        const res = await axios.get(`${API_ROOT}/categorization`);
        setCategorizationDetails(res.data);
        setNoActiveCategorization(false);
      } catch (err: any) {
        setCategorizationDetails(undefined);

        if (axios.isAxiosError(err) && err.response?.status === 409) {
          setNoActiveCategorization(true);
        }
      }
    };

    const updateInitialTasklist = async () => {
      try {
        const res = await axios.get(`${API_ROOT}/tasks/initial/raw`);
        const base = res.data as Array<Task>;
        setBaseInitialTasklist(base);

        // If not every initial requirement is met, force the "Wymagania wstępne" tab.
        const applied = computeInitialTasks(base, overlay);
        if (applied.length > 0 && applied.reduce((prev, x) => (x.value ? prev + 1 : prev), 0) < applied.length) {
          setActiveCategory(-1);
        }
      } catch (err: any) {
        setBaseInitialTasklist([]);
      }
    };

    const updateTasklist = async () => {
      try {
        const res = await axios.get(`${API_ROOT}/tasks/raw`);
        setBaseTasklist(res.data);
      } catch (err: any) {
        setBaseTasklist([]);
      }
    };

    const renderableCategories = [
      {
        id: -1,
        name: 'Wymagania wstępne',
        tasks: initialTasklist,

        lesnaThreshold: -1,
        puszczanskaThreshold: -1,

        collectedSplitPoints: -1,
        maxSplitPoints: -1,
        maxFilteredSplitPoints: -1,

        achievedSymbol: "POLOWA" as ('PUSZCZANSKA' | 'LESNA' | 'POLOWA'),
      },
      {
        id: 0,
        name: 'Podsumowanie',
        tasks: tasklist.flatMap(cat => cat.tasks),

        lesnaThreshold: -1,
        puszczanskaThreshold: -1,

        collectedSplitPoints: -1,
        maxSplitPoints: -1,
        maxFilteredSplitPoints: -1,

        achievedSymbol: "POLOWA" as ('PUSZCZANSKA' | 'LESNA' | 'POLOWA'),
      },
      ...tasklist
    ];

    // --- Edits: write to the local overlay instead of the server ---

    const toggleInitialTask = (taskId: number, state: boolean) => {
      setOverlay(o => ({
        ...o,
        initial: { ...o.initial, [taskId]: state },
      }));
    };

    const toggleMyTask = (taskId: number, state: boolean) => {
      setOverlay(o => ({
        ...o,
        tasks: {
          ...o.tasks,
          [taskId]: { value: o.tasks[taskId]?.value ?? 0, favourite: state },
        },
      }));
    };

    const updateTask = (taskId: number, value: string) => {
      const numValue = (value === 'true' ? 1 : value === 'false' ? 0 : value === "" ? 0 : parseFloat(value));
      if (isNaN(numValue) || numValue < 0) return;

      setOverlay(o => ({
        ...o,
        tasks: {
          ...o.tasks,
          [taskId]: { value: numValue, favourite: o.tasks[taskId]?.favourite ?? false },
        },
      }));
    };

    const resetSheet = () => {
      setOverlay(emptyOverlay());
      setActiveCategory(-1);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
      }
    };

    return (
      <NavbarOverlay userinfo={userinfo}>
        {noActiveCategorization ?
        <div className="dashboard-layout" style={{ height: 'calc(100vh - 56px)' }}>
          <div className="row g-4 mt-4">
            <div className="col-12">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <h5 className="text-center m-3">Aktualnie nie ma trwającej kategoryzacji. Zapraszamy później!</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
        :
        <div className="dashboard-layout" style={{ height: 'calc(100vh - 56px)' }}>
          <div className="d-flex flex-column flex-lg-row h-100">
            {/* Desktop Sidebar */}
            <DummySidebar type="desktop" renderableCategories={renderableCategories} initialLock={initialTasklist.reduce((prev, x) => (x.value ? prev+1 : prev), 0) < initialTasklist.length} myTasksMode={showStarredOnly} setMyTasksMode={setShowStarredOnly} activeCategory={activeCategory} setActiveCategory={setActiveCategory}/>

            {/* Main Content */}
            <div className="flex-grow-1 overflow-auto p-4">
              {/* Mobile Header */}
              <DummySidebar type="mobile" renderableCategories={renderableCategories} initialLock={initialTasklist.reduce((prev, x) => (x.value ? prev+1 : prev), 0) < initialTasklist.length} myTasksMode={showStarredOnly} setMyTasksMode={setShowStarredOnly} activeCategory={activeCategory} setActiveCategory={setActiveCategory}/>

              {/* Team information */}
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <h3 className="text-center">Arkusz jednostki Tu pojawi się nazwa twojej drużyny <button className="btn btn-dark btn-sm" onClick={resetSheet}>Resetuj arkusz</button></h3>
                      <p className="text-center mb-0">Przykładowa Chorągiew Harcerzy</p>
                      <p className="text-center fst-italic text-danger mb-0">To jest przykładowy arkusz. Wprowadzone zmiany nie są zapisywane</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Stats Cards */}
              {!(initialTasklist.reduce((prev, x) => (x.value ? prev+1 : prev), 0) < initialTasklist.length) &&
                <DummyStatsBar activeCategory={activeCategory <= 0 ? null : renderableCategories.filter((cat) => cat.id === activeCategory)[0]} categoryInfo={categoryInfo} />
              }

              {/* Task List */}
              <div className="task-list">
                {activeCategory === 0 ?
                  <DummySummaryLayout categories={tasklist} myTasksMode={showStarredOnly} toggleMyTask={toggleMyTask} updateTask={updateTask} locked={locked} />
                  : activeCategory === -1 ?
                  <DummyInitialTasksLayout tasks={initialTasklist} toggleInitialTask={toggleInitialTask} locked={locked} />
                  :
                  renderableCategories.filter((cat) => cat.id === activeCategory).map((cat) =>
                    <DummyCategoryLayout categories={tasklist} category={cat} myTasksMode={showStarredOnly} toggleMyTask={toggleMyTask} updateTask={updateTask} locked={locked} />
                  )
                }
              </div>
            </div>
          </div>

          <style>{`
            .nav-underline .nav-link.active {
              border-bottom: 2px solid #0d6efd;
              color: #0d6efd !important;
            }
            .dashboard-layout {
              overflow: hidden;
            }
          `}</style>
        </div>}
      </NavbarOverlay>
    );
  };

  export default DummyKategoryzacja;
